import React, { useState } from 'react';
import { Tabs, Tab } from '@material-ui/core';

import SimpleTooltip from '@root/components/tooltips/SimpleTooltip';
import DocumentView from '@root/components/DocumentView';
import DocumentControls from '@root/views/File/components/DocumentControls';
import { filterByWorkflowStatus } from '@root/resources/file/file.helpers';
import FreeTrialEndModal from '@root/components/FreeTrialEndModal/FreeTrialEndModal';
import GenerateButtonWithCap from '@root/views/File/components/GenerateButtonWithCap';
import billingResource from '@root/resources/billing';
import BoosteAltTooltipContent from '@root/components/tooltips/BoosteAltTooltipContent';
import FilterSelect from '@root/views/File/components/SelectComponents/FilterSelect/FilterSelect';

import SelectButton from './components/SelectButton';
import useStyles from './Articles.styles';

const ARTICLES_DISABLED = true;
const TEMPLATE_NAME = 'Article Writing Assistant';
const htmlTagRegex = /(<([^>]+)>)/gi;

const generateArticle = ({
  blogPostsLeft,
  setBlogRunModalOpen,
  doc,
  documents,
  generate,
  fileData,
}) => {
  if (blogPostsLeft <= 0) {
    setBlogRunModalOpen(true);
    return;
  }
  let array = [];

  if (
    doc.content.content.includes(
      '\n',
    )
  ) {
    const longArray = doc.content.content
      .replace(htmlTagRegex, '')
      .split('\n');
    longArray.pop();

    array = longArray;
  } else {
    array = [
      ...doc.content.content
        .replace(htmlTagRegex, '')
        .split(/(?=[0-9]. )/g),
    ];

    const conclusion = doc.content.content
      .replace(htmlTagRegex, '')
      .split(/(?=Conclusion: )/g)[1];

    if (conclusion) {
      array.push(conclusion);
    }
  }

  generate({
    newFileData: {
      topic:
        fileData.topic
        || documents[0].content.content
          .trim()
          .split('\n')[0]
          .replace(htmlTagRegex, ''),
      title: fileData.title
        ? fileData.title.replace(htmlTagRegex, '')
        : documents[0].content.content
          .trim()
          .split('\n')[0]
          .replace(htmlTagRegex, ''),
      description: fileData.description
        ? fileData.description.replace(htmlTagRegex, '')
        : documents[0].content.content
          .trim()
          .split('\n')[0]
          .replace(htmlTagRegex, ''),
      outline: array.slice(0, 5),
      tabIndex: '2',
    },
    generateData: {
      tabIndex: '2',
    },
  });
};

const ArticlesLayout = (props) => {
  const {
    fieldsComponent,
    generate,
    documents,
    fileData,
    checkedDocumentsIds,
    copyDocument,
    hasTeamPermissions,
    flaggedDocumentsIds,
    toggleFlagState,
    documentControlsOptions,
    canShowFilterSelect,
  } = props;
  const classes = useStyles();
  const [isTabIndexApplied, setIsTabIndexApplied] = useState(false);
  const [statusFilters, setStatusFilters] = useState(['all', 'all', 'all']);

  const handleStatusFilterChange = (index) => ({ value }) => {
    setStatusFilters([
      ...statusFilters.slice(0, index),
      value,
      ...statusFilters.slice(index + 1),
    ]);
  };

  const [tabIndex, setTabIndex] = useState(0);
  // 0 blog credits modal
  const [blogRunsModalOpen, setBlogRunModalOpen] = useState(false);
  const {
    data: {
      blogPostLimit: blogPostsLeft = 0,
      limit: creditsLeft,
    } = {},
  } = billingResource.useLimits();

  if (!isTabIndexApplied) {
    setTabIndex(parseInt(fileData.tabIndex, 10) || 0);
    setIsTabIndexApplied(true);
  }

  const isNoBlogOutlines = documents.every((d) => d.content.type !== 'blogOutline');
  const isNoBlogPosts = documents.every((d) => d.content.type !== 'blogPost');

  return (
    <>
      <div className={classes.content}>
        <DocumentControls
          actions={[]}
          disableSelect
          onAssign={documentControlsOptions.onAssign}
          canManageDocuments={documentControlsOptions.canManageDocuments}
          assignedToId={documentControlsOptions.assignedToId}
          assignedToError={documentControlsOptions.assignedToError}
          workflowStatusError={documentControlsOptions.workflowStatusError}
          workflowStatus={documentControlsOptions.workflowStatus}
          onFileChange={documentControlsOptions.onFileChange}
          isDocumentsFlagged={documentControlsOptions.isDocumentsFlagged}
        />
        <Tabs
          variant="fullWidth"
          indicatorColor="primary"
          value={tabIndex}
          onChange={(e, value) => {
            setTabIndex(value);
          }}
        >
          <Tab
            id="Blog Ideas"
            value={0}
            label="Blog Ideas"
          />
          <Tab
            id="Blog Outline"
            value={1}
            disabled={isNoBlogOutlines}
            label={isNoBlogOutlines ? (
              <SimpleTooltip
                content="Generate blog ideas and select one first!"
                placement="bottom"
              >
                <span style={{ width: '100%', fontSize: '1rem' }}>
                  Blog Outline
                </span>
              </SimpleTooltip>
            ) : (
              <span>Blog Outline</span>
            )}
          />

          <Tab
            id="Blog Post"
            value={2}
            disabled={isNoBlogPosts}
            label={isNoBlogPosts ? (
              <SimpleTooltip
                content="Generate blog outlines and select one first!"
                placement="bottom"
              >
                <span style={{ width: '100%', fontSize: '1rem' }}>
                  Blog Post
                </span>
              </SimpleTooltip>
            ) : (
              <span>Blog Post</span>
            )}
          />
        </Tabs>

        {tabIndex === 0 && (
          <>
            {fieldsComponent}
            <div className={classes.generateWrap}>
              <GenerateButtonWithCap
                generateHandler={() => generate({
                  newFileData: {
                    tabIndex: '0',
                  },
                  generateData: {
                    tabIndex: '0',
                  },
                })}
                documentsExist={documents.length > 0}
                numOriginalContent="10"
                disabled={ARTICLES_DISABLED}
                customDisableTooltipContent={ARTICLES_DISABLED
                  && <BoosteAltTooltipContent templateName={TEMPLATE_NAME} />}
              />
            </div>
            {canShowFilterSelect && (
              <FilterSelect
                status={statusFilters[0]}
                onChange={handleStatusFilterChange(0)}
              />
            )}
            <div>
              {filterByWorkflowStatus({
                documents,
                statusFilter: statusFilters[0],
                flaggedDocumentsIds,
              })
                .filter((doc) => doc.content.type === 'blogIdea')
                .map((doc) => {
                  const generationObject = {
                    newFileData: {
                      topic: fileData.topic,
                      title: doc.content.content
                        .replace(htmlTagRegex, '')
                        .split('Blog Intro:')[0]
                        .trim(),
                      description: doc.content.content
                        .replace(htmlTagRegex, '')
                        .split('Blog Intro:')[1]
                        ? doc.content.content
                          .replace(htmlTagRegex, '')
                          .split('Blog Intro:')[1]
                        : doc.content.content
                          .replace(htmlTagRegex, '')
                          .split('Blog Intro:')[0],
                      tabIndex: '1',
                    },
                    generateData: {
                      tabIndex: '1',
                    },
                  };
                  return (
                    <div
                      className={classes.documentViewWrapper}
                      key={doc._id}
                    >
                      <DocumentView
                        key={doc._id}
                        document={doc}
                        selected={checkedDocumentsIds.includes(doc._id)}
                        onFlag={toggleFlagState(doc._id)}
                        flagged={flaggedDocumentsIds.includes(doc._id)}
                        wordCount={[{
                          title: 'Content',
                          value: doc.content.content,
                        }]}
                        options={{
                          showChar: true,
                          canEdit: true,
                          canDelete: true,
                          canLike: true,
                          canCopy: true,
                          canFlag: hasTeamPermissions,
                        }}
                        onCopy={copyDocument(doc)}
                      />
                      <div className={classes.selectButtonWrapper}>
                        <SimpleTooltip
                          wrapped
                          content="This generation will use 1 credit , and generate 5 - 10 pieces of new content."
                          placement="right"
                          disableHoverListener={ARTICLES_DISABLED}
                        >
                          <SelectButton
                            generate={() => generate(generationObject)}
                            setTabIndex={() => setTabIndex(1)}
                            disabled={ARTICLES_DISABLED || creditsLeft <= 0}
                            customTooltip={ARTICLES_DISABLED
                              && <BoosteAltTooltipContent templateName={TEMPLATE_NAME} />}
                          />
                        </SimpleTooltip>
                      </div>
                    </div>

                  );
                })}
            </div>
          </>
        )}
        {tabIndex === 1 && (
          <div>
            {canShowFilterSelect && (
              <FilterSelect
                status={statusFilters[1]}
                onChange={handleStatusFilterChange(1)}
              />
            )}
            {filterByWorkflowStatus({
              documents,
              statusFilter: statusFilters[1],
              flaggedDocumentsIds,
            })
              .filter((doc) => doc.content.type === 'blogOutline').map((doc) => (
                <div
                  className={classes.documentViewWrapper}
                  key={doc._id}
                >
                  <DocumentView
                    key={doc._id}
                    document={doc}
                    selected={checkedDocumentsIds.includes(doc._id)}
                    onFlag={toggleFlagState(doc._id)}
                    flagged={flaggedDocumentsIds.includes(doc._id)}
                    wordCount={[{
                      title: 'Content',
                      value: doc.content.content,
                    }]}
                    options={{
                      showChar: true,
                      canEdit: true,
                      canDelete: true,
                      canLike: true,
                      canCopy: true,
                      canFlag: hasTeamPermissions,
                    }}
                    onCopy={copyDocument(doc)}
                  />
                  <div className={classes.selectButtonWrapper}>
                    <SimpleTooltip
                      content={`This generation will use 1 blog credit.
                          A maximum of 5 sections can be used as inputs in one run. 
                          Each section will take about 20 seconds to generate.`}
                      placement="right"
                      wrapped
                      disableHoverListener={ARTICLES_DISABLED}
                    >
                      <SelectButton
                        generate={() => generateArticle({
                          blogPostsLeft,
                          setBlogRunModalOpen,
                          doc,
                          documents,
                          generate,
                          fileData,
                        })}
                        setTabIndex={() => setTabIndex(2)}
                        disabled={ARTICLES_DISABLED || blogPostsLeft <= 0}
                        customTooltip={ARTICLES_DISABLED
                          && <BoosteAltTooltipContent templateName={TEMPLATE_NAME} />}
                      />
                    </SimpleTooltip>
                  </div>
                </div>
              ))}
          </div>
        )}
        {tabIndex === 2 && (
          <div>
            {canShowFilterSelect && (
              <FilterSelect
                status={statusFilters[2]}
                onChange={handleStatusFilterChange(2)}
              />
            )}
            {filterByWorkflowStatus({
              documents,
              statusFilter: statusFilters[2],
              flaggedDocumentsIds,
            })
              .filter((doc) => doc.content.type === 'blogPost').map((doc) => (
                <DocumentView
                  key={doc._id}
                  document={doc}
                  selected={checkedDocumentsIds.includes(doc._id)}
                  onFlag={toggleFlagState(doc._id)}
                  flagged={flaggedDocumentsIds.includes(doc._id)}
                  wordCount={[{
                    title: 'Content',
                    value: doc.content.content,
                  }]}
                  options={{
                    showChar: true,
                    canEdit: true,
                    canDelete: true,
                    canLike: true,
                    canCopy: true,
                    canFlag: hasTeamPermissions,
                  }}
                  onCopy={copyDocument(doc)}
                />
              ))}
          </div>
        )}
      </div>
      <FreeTrialEndModal
        modalTitle="Looks like you've used all your blog runs!"
        isOpen={blogRunsModalOpen}
        setIsOpen={setBlogRunModalOpen}
      />
    </>
  );
};

export default ArticlesLayout;
